import React, { useState, useEffect } from 'react';
import { Calendar, Upload, Plus, Edit, Trash2, Download } from 'lucide-react';
import api from '../../services/api';
import PageHeader from '../../components/PageHeader';
import { crumbsFor } from '../../navigation/modules';

const HolidayManagement = () => {
    const [holidays, setHolidays] = useState([]);
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
    const [showAddModal, setShowAddModal] = useState(false);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadResult, setUploadResult] = useState(null);
    const [fileInputKey, setFileInputKey] = useState(0);
    const [loading, setLoading] = useState(false);
    const currentYear = new Date().getFullYear();
    const yearOptions = [currentYear - 1, currentYear, currentYear + 1, currentYear + 2];

    useEffect(() => {
        fetchHolidays();
    }, [selectedYear]);

    const fetchHolidays = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/holidays?year=${selectedYear}`);
            setHolidays(res.data);
        } catch (error) {
            console.error('Error fetching holidays:', error);
            alert('Failed to fetch holidays');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (e) => {
        e.preventDefault();
        if (!uploadFile) {
            alert('Please select a file');
            return;
        }

        setUploading(true);
        setUploadResult(null);
        const formData = new FormData();
        formData.append('file', uploadFile);

        try {
            const res = await api.post('/holidays/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setUploadResult({ ok: true, ...res.data });
            setUploadFile(null);
            setFileInputKey(k => k + 1); // clear the input so the same file can be re-selected
            fetchHolidays();
        } catch (error) {
            console.error('Upload error:', error);
            setUploadResult({ ok: false, message: error.response?.data?.error || 'Failed to upload file' });
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteHoliday = async (id) => {
        if (!window.confirm('Are you sure you want to delete this holiday?')) return;

        try {
            await api.delete(`/holidays/${id}`);
            alert('Holiday deleted successfully');
            fetchHolidays();
        } catch (error) {
            console.error('Delete error:', error);
            alert('Failed to delete holiday');
        }
    };

    const downloadSampleTemplate = () => {
        const y = currentYear + 1;
        const csvContent = [
            'Date,Holiday Name,Type,Description',
            `26-01-${y},Republic Day,Public,National holiday`,
            `15-08-${y},Independence Day,Public,National holiday`,
            `02-10-${y},Gandhi Jayanti,Public,National holiday`,
            `25-12-${y},Christmas,Optional,Restricted holiday - employees may choose`,
        ].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'holiday_template.csv';
        a.click();
    };

    return (
        <div className="container section">
            <PageHeader crumbs={crumbsFor('holidays')} />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
                <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--text-primary)' }}>
                    Holiday Management
                </h1>

                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <select
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                        style={{ padding: '0.5rem 1rem', borderRadius: '0.5rem', border: '1px solid var(--border)' }}
                    >
                        {yearOptions.map(year => (
                            <option key={year} value={year}>{year}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Upload Section */}
            <div className="glass-card" style={{ padding: '1.5rem', marginBottom: '2rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Upload size={20} /> Upload Holiday Calendar
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
                    Upload an Excel (.xlsx, .xls) or CSV file. The first row must be headers: <strong>Date</strong> and{' '}
                    <strong>Holiday Name</strong> are required; <strong>Type</strong> (Public / Optional) and{' '}
                    <strong>Description</strong> are optional. Dates as DD-MM-YYYY, YYYY-MM-DD or real Excel dates.
                </p>

                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <input
                        key={fileInputKey}
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => { setUploadFile(e.target.files[0]); setUploadResult(null); }}
                        style={{ flex: 1, minWidth: '200px' }}
                    />
                    <button
                        onClick={handleFileUpload}
                        disabled={!uploadFile || uploading}
                        className="btn btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <Upload size={18} />
                        {uploading ? 'Uploading...' : 'Upload'}
                    </button>
                    <button
                        onClick={downloadSampleTemplate}
                        className="btn btn-secondary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <Download size={18} />
                        Download Template
                    </button>
                </div>

                {uploadResult && (
                    <div style={{
                        marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '0.5rem', fontSize: '0.875rem',
                        background: !uploadResult.ok ? '#fee2e2' : uploadResult.skipped > 0 ? '#fef3c7' : '#dcfce7',
                        color: !uploadResult.ok ? '#991b1b' : uploadResult.skipped > 0 ? '#92400e' : '#166534',
                    }}>
                        <div style={{ fontWeight: 600 }}>{uploadResult.message}</div>
                        {uploadResult.skippedRows?.length > 0 && (
                            <>
                                <div style={{ marginTop: '0.5rem' }}>Skipped rows:</div>
                                <ul style={{ margin: '0.25rem 0 0 1.25rem', maxHeight: '12rem', overflowY: 'auto' }}>
                                    {uploadResult.skippedRows.map((s, i) => (
                                        <li key={i}>Row {s.row}: {s.reason}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                        {uploadResult.warnings?.length > 0 && (
                            <>
                                <div style={{ marginTop: '0.5rem' }}>Imported with notes:</div>
                                <ul style={{ margin: '0.25rem 0 0 1.25rem' }}>
                                    {uploadResult.warnings.map((w, i) => (
                                        <li key={i}>Row {w.row}: {w.message}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                )}
            </div>

            {/* Holidays List */}
            <div className="glass-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: '600' }}>
                        Holidays for {selectedYear}
                    </h2>
                    <button
                        onClick={() => setShowAddModal(true)}
                        className="btn btn-primary"
                        style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                    >
                        <Plus size={18} /> Add Holiday
                    </button>
                </div>

                {loading ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                        Loading holidays...
                    </div>
                ) : holidays.length === 0 ? (
                    <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                        No holidays found for {selectedYear}. Upload a calendar or add holidays manually.
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid #e5e7eb' }}>
                            <tr>
                                <th style={{ padding: '1rem', textAlign: 'left' }}>Date</th>
                                <th style={{ padding: '1rem', textAlign: 'left' }}>Holiday Name</th>
                                <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                                <th style={{ padding: '1rem', textAlign: 'left' }}>Description</th>
                                <th style={{ padding: '1rem', textAlign: 'right' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {holidays.map(holiday => (
                                <tr key={holiday.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ padding: '1rem', fontWeight: '500' }}>
                                        {new Date(holiday.date).toLocaleDateString('en-IN', {
                                            weekday: 'short',
                                            day: 'numeric',
                                            month: 'short'
                                        })}
                                    </td>
                                    <td style={{ padding: '1rem' }}>{holiday.name}</td>
                                    <td style={{ padding: '1rem' }}>
                                        <span style={{
                                            padding: '0.25rem 0.75rem',
                                            borderRadius: '999px',
                                            fontSize: '0.75rem',
                                            fontWeight: '600',
                                            background: holiday.type === 'PUBLIC' ? '#dbeafe' : '#fef3c7',
                                            color: holiday.type === 'PUBLIC' ? '#1e40af' : '#92400e'
                                        }}>
                                            {holiday.type}
                                        </span>
                                    </td>
                                    <td style={{ padding: '1rem', color: 'var(--text-muted)' }}>
                                        {holiday.description || '-'}
                                    </td>
                                    <td style={{ padding: '1rem', textAlign: 'right' }}>
                                        <button
                                            onClick={() => handleDeleteHoliday(holiday.id)}
                                            style={{ color: '#ef4444', border: 'none', background: 'none', cursor: 'pointer' }}
                                            title="Delete Holiday"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Add Holiday Modal */}
            {showAddModal && (
                <AddHolidayModal
                    onClose={() => setShowAddModal(false)}
                    onSuccess={() => {
                        setShowAddModal(false);
                        fetchHolidays();
                    }}
                    year={selectedYear}
                />
            )}
        </div>
    );
};

// Add Holiday Modal Component
const AddHolidayModal = ({ onClose, onSuccess }) => {
    const [formData, setFormData] = useState({
        date: '',
        name: '',
        type: 'PUBLIC',
        description: ''
    });
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await api.post('/holidays', formData);
            alert('Holiday added successfully');
            onSuccess();
        } catch (error) {
            console.error('Add holiday error:', error);
            alert(error.response?.data?.error || 'Failed to add holiday');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: 'var(--surface)', padding: '2rem', borderRadius: '1rem', width: '100%', maxWidth: '500px' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>Add Holiday</h2>
                <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                        <label htmlFor="holiday-date" style={{ display: 'block', marginBottom: '0.5rem' }}>Date</label>
                        <input
                            id="holiday-date"
                            type="date"
                            required
                            value={formData.date}
                            onChange={e => setFormData({ ...formData, date: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.5rem' }}
                        />
                    </div>
                    <div>
                        <label htmlFor="holiday-name" style={{ display: 'block', marginBottom: '0.5rem' }}>Holiday Name</label>
                        <input
                            id="holiday-name"
                            type="text"
                            required
                            value={formData.name}
                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.5rem' }}
                        />
                    </div>
                    <div>
                        <label htmlFor="holiday-type" style={{ display: 'block', marginBottom: '0.5rem' }}>Type</label>
                        <select
                            id="holiday-type"
                            value={formData.type}
                            onChange={e => setFormData({ ...formData, type: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.5rem' }}
                        >
                            <option value="PUBLIC">Public Holiday</option>
                            <option value="RESTRICTED">Restricted / Optional Holiday</option>
                        </select>
                    </div>
                    <div>
                        <label htmlFor="holiday-description" style={{ display: 'block', marginBottom: '0.5rem' }}>Description (Optional)</label>
                        <textarea
                            id="holiday-description"
                            rows="2"
                            value={formData.description}
                            onChange={e => setFormData({ ...formData, description: e.target.value })}
                            style={{ width: '100%', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: '0.5rem' }}
                        />
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                        <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
                        <button type="submit" disabled={loading} className="btn btn-primary">
                            {loading ? 'Adding...' : 'Add Holiday'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default HolidayManagement;
